import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { uncoveryApi } from "services";

const collectionNavAdapter = createEntityAdapter();
const initialState = collectionNavAdapter.getInitialState({
  fetchAllStatus: "idle",
  fetchAllErrorMessage: "",
  data: [],
  hasNextPage: false,
  page: 0,
  pageSize: 50,
  sortBy: "name",
  orderBy: "asc",
  searchTerm: "",
});

export const fetchAllCollections = createAsyncThunk(
  "collectionNav/fetchAllCollections",
  async ({ entityId, mapId }, { rejectWithValue, getState }) => {
    const { page, pageSize, sortBy, orderBy, searchTerm } =
      getState().collectionNav;
    const query = `/entities/${entityId}/assets/collections?cartographyId=${mapId}&page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const collectionNavSlice = createSlice({
  name: "collectionNav",
  initialState,
  reducers: {
    updateSearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.page = 1;
      state.searchTerm = searchTerm;
      return state;
    },
    sort: (state, { payload }) => {
      const { sortBy } = payload;
      state.page = 1;
      if (sortBy !== state.sortBy) {
        state.orderBy = "asc";
        state.sortBy = sortBy;
      } else {
        state.orderBy = state.orderBy === "desc" ? "asc" : "desc";
      }
      return state;
    },
    updatePage: (state, { payload }) => {
      const { page } = payload;
      state.page = page;
      return state;
    },
    removeOneCollectionNav: (state, { payload }) => {
      const { collectionId } = payload;
      state.data = state.data.filter((c) => c.id !== collectionId);
      return state;
    },
    addOneCollectionNav: (state, { payload }) => {
      const { collection } = payload;
      state.data = [collection].concat(state.data);
      return state;
    },
    updateOneCollectionNav: (state, { payload }) => {
      const { collection } = payload;
      const index = state.data.findIndex((c) => c.id === collection.id);
      state.data[index] = collection;
      return state;
    },
    resetCollectionNavState: () => initialState,
  },
  extraReducers: {
    [fetchAllCollections.pending]: (state, { payload }) => {
      state.fetchAllStatus = "loading";
    },
    [fetchAllCollections.fulfilled]: (state, { payload }) => {
      if (state.page === 1) {
        state.data = payload.data;
      } else {
        state.data = [...state.data, ...payload.data];
      }
      state.hasNextPage = payload.pageInfo.hasNextPage;
      state.fetchAllStatus = "success";
    },
    [fetchAllCollections.rejected]: (state, { payload }) => {
      state.fetchAllErrorMessage = payload.message;
      state.fetchAllStatus = "error";
    },
  },
});

export const {
  updateSearchTerm,
  sort,
  updatePage,
  removeOneCollectionNav,
  addOneCollectionNav,
  updateOneCollectionNav,
  resetCollectionNavState,
} = collectionNavSlice.actions;

export default collectionNavSlice.reducer;

const collectionNavState = (state) => state.collectionNav;

export const selectCollectionIds = createSelector(
  collectionNavState,
  (collections) => collections.data.map((collection) => collection.id)
);
export const selectCollections = createSelector(
  collectionNavState,
  (collections) => collections.data
);
export const selectCollectionById = createSelector(
  [collectionNavState, (collectionNavState, collectionId) => collectionId],
  (collections, collectionId) =>
    collections.data.find((collection) => collection.id === collectionId)
);
